/** Max anonymous Q&A-driven refinements per template (see Story 2.3) */
export const ANONYMOUS_REFINEMENT_LIMIT = 5

const STORAGE_KEY_PREFIX = 'diy_anon_refinement_count_'

export function getAnonymousRefinementCountKey(templateSlug: string): string {
  return `${STORAGE_KEY_PREFIX}${templateSlug}`
}

/**
 * Anonymous-only bucket (capped writes at 5). Authenticated sessions do not increment
 * this key — see `useAnonymousRefinementUsage`.
 * If key missing, backfill 0. Raw read is uncapped only if storage was ever corrupted;
 * use {@link clampAnonymousRefinementCount} for display when needed.
 */
export function readAnonymousRefinementCountFromStorage(
  templateSlug: string
): number {
  if (typeof localStorage === 'undefined') return 0
  try {
    const raw = localStorage.getItem(getAnonymousRefinementCountKey(templateSlug))
    if (raw == null) return 0
    const n = Number.parseInt(raw, 10)
    if (Number.isNaN(n)) return 0
    return Math.max(0, n)
  } catch {
    return 0
  }
}

export function clampAnonymousRefinementCount(n: number): number {
  return Math.max(0, Math.min(ANONYMOUS_REFINEMENT_LIMIT, n))
}

/** Persist count; for anonymous cap use `max: ANONYMOUS_REFINEMENT_LIMIT` */
export function writeAnonymousRefinementCountToStorage(
  templateSlug: string,
  count: number,
  options?: { max: number }
): void {
  if (typeof localStorage === 'undefined') return
  try {
    const floored = Math.max(0, count)
    const v =
      options !== undefined
        ? Math.min(floored, options.max)
        : floored
    localStorage.setItem(getAnonymousRefinementCountKey(templateSlug), String(v))
  } catch {
    // ignore quota / private mode
  }
}

export function isMaterialPlanChange(
  previousHash: string,
  nextHash: string
): boolean {
  return previousHash !== nextHash
}
